/**
 * @fileoverview `<FormField>` — wrapper standard d'un champ de formulaire GNPC.
 *
 * Compose ensemble :
 *   - un `<Label>` accessible (associé via `htmlFor` / `id` généré) ;
 *   - un `<Tooltip>` optionnel à côté du label (icône `HelpCircle` sur
 *     laquelle l'utilisateur pointe pour lire l'aide contextuelle) ;
 *   - l'`<Input>` (ou tout autre `children` : Textarea, Select…) ;
 *   - une `action` optionnelle alignée sur le contrôle (ex : bouton rechercher) ;
 *   - un `hint` sous le champ (aide brève, toujours visible) ;
 *   - un `error` sous le champ, avec icône `AlertCircle`, quand la
 *     valeur est invalide. Passe automatiquement `aria-invalid` et
 *     `aria-describedby` au child pour l'accessibilité.
 *
 * Convention : préférer `<FormField>` à un `<Label>` + `<Input>` bruts
 * dans toutes les pages métier — assure la cohérence a11y et visuelle.
 *
 * @see Documents/Paymetrust_Guide_Integration_Frontend_v1.pdf §6.2
 */

import { cloneElement, isValidElement, useId, type ReactElement, type ReactNode } from 'react';
import { AlertCircle, HelpCircle } from 'lucide-react';
import { Label } from './ui/label.tsx';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip.tsx';
import { cn } from '../lib/utils.ts';

/**
 * Petit utilitaire — la prop `rightIcon` n'est valide QUE sur les Input
 * GNPC (qui expose ce slot). On refuse de la pousser sur un `<div>` ou
 * un `<Select>` : React logue autrement un warning `unknown DOM attribute`.
 * Détecte l'Input GNPC via son `data-slot` conventionnel (attribut mis
 * par les composants shadcn de packages/ui).
 */
function acceptsRightIcon(el: ReactElement<Record<string, unknown>>): boolean {
  const slot =
    el.type && typeof el.type === 'object' && 'displayName' in el.type
      ? (el.type as { displayName?: string }).displayName
      : undefined;
  return slot === 'Input' || el.props?.['data-slot'] === 'input';
}

export interface FormFieldProps {
  /**
   * Libellé affiché au-dessus du champ. Doit décrire précisément ce que
   * le champ attend (ex : « E-mail professionnel », pas « Email »).
   */
  label: string;
  /**
   * Marqueur optionnel de champ obligatoire. Affiche un `*` orange après
   * le libellé. L'obligation elle-même reste portée par le composant
   * enfant (attribut HTML `required` ou validation zod).
   */
  required?: boolean;
  /**
   * Aide contextuelle enrichie affichée dans un tooltip déclenché par
   * l'icône `HelpCircle` à côté du label. Utile pour les règles métier
   * détaillées (« La plaque suit le format 2 lettres, 3 chiffres, 2 lettres »)
   * qui alourdiraient le hint permanent. Si `undefined`, l'icône n'apparaît pas.
   */
  tooltip?: ReactNode;
  /**
   * Message d'aide court, toujours visible sous le champ. Ne pas dupliquer
   * l'info du label. Priorité : `error` masque `hint` s'il est présent.
   */
  hint?: ReactNode;
  /**
   * Message d'erreur — quand présent, marque le champ `aria-invalid` et
   * masque le `hint`. Affiche l'icône `AlertCircle` en rouge à droite du
   * champ (via prop `rightIcon` transmise à `<Input>`).
   */
  error?: ReactNode;
  /**
   * Le composant de saisie (`<Input>`, `<Textarea>`, `<Select>`…). Reçoit
   * automatiquement `id`, `aria-invalid`, `aria-describedby` calculés ici.
   */
  children: ReactElement<{
    id?: string;
    'aria-invalid'?: boolean;
    'aria-describedby'?: string;
    rightIcon?: ReactNode;
  }>;
  /**
   * Action placée à droite du contrôle sur desktop et sous le contrôle sur
   * mobile. À utiliser pour un bouton directement lié à la saisie.
   */
  action?: ReactNode;
  /**
   * Id du contrôle focusable réel, quand le child direct ne rend PAS
   * lui-même le contrôle. Cas type : le `<Select>` Radix — son Root ne rend
   * aucun nœud DOM et avale les props inconnues (`id`, `aria-*`), si bien
   * que le `htmlFor` du label pointerait vers un id absent du document
   * (violation W3C « label for must be the ID of a non-hidden form
   * control »). Fournir ici l'id posé manuellement sur le contrôle focusable
   * (ex : `<SelectTrigger id="...">`) ; `FormField` n'injecte alors plus
   * rien sur le child et se contente d'associer le label à cet id.
   */
  controlId?: string;
  /** Classe supplémentaire sur le conteneur racine. */
  className?: string;
}

/**
 * Champ de formulaire standardisé — Label + tooltip d'aide + input + hint / error.
 *
 * L'ID du champ est généré automatiquement via `useId()` — pas besoin
 * d'en fournir un manuellement, mais si l'enfant en a déjà un, il est
 * préservé (utile pour lier des refs externes).
 */
export function FormField({
  label,
  required,
  tooltip,
  hint,
  error,
  className,
  children,
  action,
  controlId,
}: FormFieldProps): React.JSX.Element {
  const generatedId = useId();
  const fieldId = controlId ?? children.props.id ?? generatedId;
  const hintId = `${fieldId}-hint`;
  const errorId = `${fieldId}-error`;
  const describedBy = error ? errorId : hint ? hintId : undefined;
  const invalid = Boolean(error);

  // Injecte id + a11y attrs. L'icône d'erreur `rightIcon` n'est propagée
  // qu'aux enfants qui l'acceptent (Input GNPC) — sinon React se plaint
  // d'un attribut DOM inconnu sur un `<div>` ou un `<Select>`.
  //
  // Avec `controlId`, on n'injecte RIEN : l'appelant a posé lui-même l'id
  // sur le contrôle focusable (ex : `<SelectTrigger id>`), et cloner l'id
  // sur un Root Radix le ferait atterrir sur un nœud caché — le label
  // pointerait alors vers un contrôle masqué (violation W3C).
  const enhancedChild =
    !controlId && isValidElement(children)
      ? cloneElement(children as ReactElement<Record<string, unknown>>, {
          id: fieldId,
          'aria-invalid': invalid || children.props['aria-invalid'],
          'aria-describedby': describedBy,
          ...(acceptsRightIcon(children)
            ? {
                rightIcon: invalid ? (
                  <AlertCircle className="text-danger" />
                ) : (
                  children.props.rightIcon
                ),
              }
            : {}),
        })
      : children;

  return (
    <div className={cn('grid w-full gap-1.5', className)}>
      <div className="flex items-center gap-1.5">
        <Label htmlFor={fieldId}>
          {label}
          {required && (
            <span aria-hidden="true" className="text-primary ml-0.5">
              *
            </span>
          )}
        </Label>
        {tooltip && (
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="text-muted-foreground hover:text-quipux-blue-600 focus-visible:ring-ring/50 inline-flex items-center rounded-full p-0.5 transition-colors focus-visible:outline-none focus-visible:ring-2"
                  aria-label={`Aide sur : ${label}`}
                >
                  <HelpCircle className="size-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right" className="max-w-xs text-sm">
                {tooltip}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>

      {action ? (
        <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center">
          {enhancedChild}
          <div className="sm:self-center">{action}</div>
        </div>
      ) : (
        enhancedChild
      )}

      {error ? (
        <p id={errorId} role="alert" className="text-danger flex items-center gap-1.5 text-xs">
          <AlertCircle className="size-3.5 shrink-0" />
          {error}
        </p>
      ) : hint ? (
        <p id={hintId} className="text-muted-foreground text-xs">
          {hint}
        </p>
      ) : null}
    </div>
  );
}
